import type { CSSProperties, ReactNode } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Play } from "lucide-react";
import { CLUB_FAQS, CLUB_INCLUDES, CLUB_SCHEDULE, JOIN_URL, PATREON_URL, THREAD_FRIEND_URL } from "@/lib/club";
import { SOCIALS } from "@/lib/social";
import { nextClasses } from "./ClubRichThread";
import { Band, Draw, EASE, Footer, Head, Heart, InkHeart, Nav, Torn, U, VIEW, datePart, useFonts } from "./HomePattern";
import "./HomeRound5.css";
import "./HomePattern.css";
import "./ClubPattern.css";

/* The Loose Threads Club page in the "Color outside the pattern" style: torn
   paper, arches, pen notes, and the club as a sewing pattern. Copy follows the
   canonical /club-preview page. */

/* Hero scene positions on a 1000 × 880 frame. */
const F = { w: 1000, h: 880 };
const at = (x: number, y: number, w?: number, h?: number): CSSProperties => ({
  left: `${(x / F.w) * 100}%`, top: `${(y / F.h) * 100}%`,
  ...(w !== undefined && { width: `${(w / F.w) * 100}%` }),
  ...(h !== undefined && { height: `${(h / F.h) * 100}%` }),
});

const TICK = "M6 12 L10 17 L20 3";

function Scene() {
  return <div className="hpc-scene">
    <svg className="hpc-scene-paper" viewBox={`0 0 ${F.w} ${F.h}`} preserveAspectRatio="none" aria-hidden="true">
      <Torn seed={17} fill="#1d3fc8" edge={[[520, 0], [560, 60], [640, 110], [760, 132], [880, 120], [1000, 150]]} close={[[1000, 0]]} />
      <Torn seed={29} fill="#f6d64a" edge={[[1000, 470], [930, 520], [880, 620], [860, 740], [880, 880]]} close={[[1000, 880]]} />
    </svg>
    <svg className="hpc-scene-dash" viewBox={`0 0 ${F.w} ${F.h}`} preserveAspectRatio="none" aria-hidden="true">
      <Draw dash d="M760 390 C 820 420, 830 460, 790 500" delay={1.3} width={2.4} />
      <Draw dash d="M40 640 C 0 560, 20 470, 90 440" delay={1.4} width={2.4} />
    </svg>

    <motion.div className="hpc-abs hpc-arch" style={at(110, 110, 440, 700)} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7, ease: EASE }}>
      <span><img src="/chelsea-and-cosmo.jpg" alt="Chelsea smiling at her sewing machine with Cosmo the cat on her lap" /></span>
      <i className="hpc-live"><b />Live</i>
    </motion.div>

    <motion.div className="hpc-abs hpc-card" style={at(600, 70, 300, 300)} initial={{ opacity: 0, y: -20, rotate: -12 }} animate={{ opacity: 1, y: 0, rotate: -5 }} transition={{ delay: 0.7, type: "spring", stiffness: 150, damping: 15 }}>
      <p className="hpc-card-title">Tuesday night:</p>
      <ul aria-label="A club night">
        {["Pick a project", "Sew with Chelsea", "Ask the question", "Show it off"].map((t, i) => <li key={t}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="2" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" />{i < 3 && <Draw d={TICK} delay={1.3 + i * 0.25} width={2.6} />}</svg>
          {t}{i === 3 && <Heart delay={2.2} />}
        </li>)}
      </ul>
    </motion.div>

    <motion.figure className="hpc-abs hp-polaroid hpc-polaroid" style={at(630, 450, 270, 380)} initial={{ opacity: 0, y: 40, rotate: 14 }} animate={{ opacity: 1, y: 0, rotate: 5 }} whileHover={{ y: -6, rotate: 2 }} transition={{ delay: 0.9, type: "spring", stiffness: 140, damping: 15 }}>
      <span className="hp-polaroid-img"><img src="/chelsea.JPG" alt="Chelsea wearing her handmade cereal-print outfit and a colorful quilted bag" /></span>
      <figcaption>I made the outfit<br />AND the bag!<Heart delay={1.7} /></figcaption>
    </motion.figure>

    <motion.p className="hpc-abs hpc-pen-note" style={at(10, 836)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>2nd &amp; 4th Tuesdays, 7 PM MT <Heart delay={2} /></motion.p>
    <motion.p className="hpc-abs hp-caps hpc-caps" style={at(0, 210)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}>Live<br />Replay<br />Repeat</motion.p>
  </div>;
}

function Hero() {
  return <section className="hp-hero hpc-hero" aria-labelledby="hpc-title">
    <div className="hpc-stage">
      <Nav club />
      <div className="hpc-hero-grid">
        <div className="hpc-copy">
          <motion.p className="hp-kicker hpc-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Loose Threads Club · on Patreon</motion.p>
          <h1 id="hpc-title" className="hpc-title">
            {["Your next project.", "A little help.", "A lot of fun."].map((w, i) => <motion.span key={w} className={`hp-h-${i}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.12, duration: 0.6, ease: EASE }}>
              {i === 2 ? <>A lot of <span className="hpc-fun">fun.<svg viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true"><Draw d="M4 12 C 80 6, 200 6, 296 9" delay={0.8} width={7} /></svg></span></> : w}
            </motion.span>)}
          </h1>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }}>
            <p className="hpc-lede">Hi, I’m Chelsea! Pull up a chair and sew with me. I’ll help you make something you’re excited about, work through the tricky bits, and have some fun along the way.</p>
            <div className="hp-actions hpc-actions">
              <a href={JOIN_URL} className="hp-btn hp-btn-blue">Come sew with me <ArrowRight size={18} aria-hidden="true" /></a>
              <a href="#inside" className="hp-btn hp-btn-line">Take a peek</a>
            </div>
            <p className="hpc-price-note"><b>$25/month</b> · Live classes + recordings · Cancel anytime</p>
            <p className="hp-pen hpc-hand">Your sewing room, my sewing room. <Heart delay={1.4} /></p>
          </motion.div>
        </div>
        <Scene />
      </div>
    </div>
  </section>;
}

/* ─── 01 This month ─── */

const STEPS = [
  { t: "Pick your fabrics", d: "Something you’ll love carrying around." },
  { t: "Sew along live", d: "Two classes, one bag, all your questions." },
  { t: "Replay the tricky bit", d: "Every class is recorded for you." },
  { t: "Show it off", d: "The club will cheer. Loudly." },
];

/** The canonical page's sample tote, recolored for this palette. */
function Tote() {
  return <svg viewBox="0 0 480 360" role="img" aria-label="Illustration of a patchwork tote, a sample project">
    <defs>
      <pattern id="hpc-patch" patternUnits="userSpaceOnUse" width="100" height="100">
        <rect width="100" height="100" fill="#fbf7f0" />
        <rect width="50" height="50" fill="#f7a1c4" />
        <rect x="50" y="50" width="50" height="50" fill="#f6d64a" />
        <path d="M50 0h50v50ZM0 50h50v50Z" fill="#1d3fc8" />
        <path d="M50 0v50h50ZM0 50v50h50Z" fill="#f2639a" />
      </pattern>
    </defs>
    <ellipse cx="240" cy="318" rx="143" ry="15" fill="#141414" opacity=".08" />
    <path d="M186 150V98a54 54 0 0 1 108 0v52" fill="none" stroke="#1d3fc8" strokeWidth="18" />
    <path d="M175 150V98a65 65 0 0 1 130 0v52" fill="none" stroke="#141414" strokeWidth="2" strokeDasharray="4 5" opacity=".5" />
    <path d="M116 126h248l-17 175q-107 21-214 0Z" fill="url(#hpc-patch)" stroke="#141414" strokeWidth="2" />
    <path d="M125 133h230l-15 161q-100 19-200 0Z" fill="none" stroke="#fbf7f0" strokeWidth="1.8" strokeDasharray="4 5" />
    <path d="M117 126h246l-2 23H119Z" fill="#141414" />
    <path d="M122 138h234" stroke="#fbf7f0" strokeWidth="1.5" strokeDasharray="4 5" />
    <rect x="186" y="222" width="108" height="32" fill="#fbf7f0" transform="rotate(-3 240 240)" />
    <text x="240" y="244" textAnchor="middle" fill="#141414" fontSize="19" fontFamily="'Nothing You Could Do', cursive">made by me</text>
  </svg>;
}

function ThisMonth() {
  return <section id="inside" className="hp-sec hpc-month" aria-labelledby="hpc-month-title">
    <div className="hp-wrap">
      <Head n="01" kicker="This month in the club" id="hpc-month-title">Let’s make your <span className="hp-pink">new favorite</span> <U>bag.</U></Head>
      <div className="hpc-month-grid">
        <motion.figure className="hpc-sheet" initial={{ opacity: 0, y: 40, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: -2 }} viewport={VIEW} transition={{ duration: 0.7, ease: EASE }}>
          <span className="hpc-sheet-label">Piece A · Tote · Cut 2</span>
          <Tote />
          <figcaption><span className="hpc-sample">Sample project</span><span className="hp-pen">A little fabric. A little help. A happy new make. <InkHeart delay={0.8} /></span></figcaption>
        </motion.figure>
        <div>
          <p className="hpc-month-lede">Let’s give a patchwork tote some personality! Pick your fabrics and sew along with me. I’ll walk you through the steps so you can enjoy making a bag you’ll reach for every day.</p>
          <ol className="hpc-steps">
            {STEPS.map((s, i) => <motion.li key={s.t} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={VIEW} transition={{ delay: 0.1 + i * 0.1, duration: 0.5, ease: EASE }}>
              <span>{i + 1}</span><b>{s.t}</b><em className="hp-pen">{s.d}</em>
            </motion.li>)}
          </ol>
          <p className="hpc-schedule"><span>Schedule</span>{CLUB_SCHEDULE}</p>
          <div className="hpc-month-join">
            <a href={PATREON_URL} target="_blank" rel="noreferrer" className="hp-btn hp-btn-line">See the current project <ArrowUpRight size={18} aria-hidden="true" /></a>
            <p className="hpc-fine">Sample project shown to preview the layout. Check Patreon for the actual project, dates, skills, and supplies. Patterns and supplies are separate unless I say otherwise.</p>
          </div>
        </div>
      </div>
    </div>
  </section>;
}

/* ─── 02 Two ways ─── */

const WAYS: { key: string; img: string; pos: string; arch: string; badge: ReactNode; tag: string; title: string; text: string; list: string[]; foot: ReactNode }[] = [
  {
    key: "live", img: "/chelsea-rainbow-portrait.jpg", pos: "58% 26%", arch: "pink", badge: <i className="hpc-live"><b />Live</i>,
    tag: "Sew with me live", title: "Your creative night in.",
    text: "Grab your fabric, settle in, and sew with me. Ask me that question. Let’s work through the tricky bit together. I’m here for the fun, the little wins, and the thread-covered chaos.",
    list: ["Ask me questions as we sew", "Let me help with the tricky steps", "Make time for something you love"],
    foot: <>{CLUB_SCHEDULE}<small>My usual class schedule, unless I announce a change.</small></>,
  },
  {
    key: "replay", img: "/img/products/qs-mat.jpg", pos: "50% 30%", arch: "yellow", badge: <i className="hpc-play"><Play size={20} fill="currentColor" aria-hidden="true" /></i>,
    tag: "Sew on your schedule", title: "Your pace. Your happy place.",
    text: "I know life gets busy. Maybe your sewing time is Sunday morning or after everyone’s in bed. I include the class recordings so you can sew along with me whenever it works for you.",
    list: ["Watch when you have time", "Pause while you cut, pin, and sew", "Replay a step until it clicks"],
    foot: <>I’d love to have you here, even if you watch later.<small>My class recordings are included in your membership.</small></>,
  },
];

function TwoWays() {
  return <Band color="#1d3fc8" seed={71} className="hp-club hpc-ways" label="hpc-ways-title">
    <div className="hp-wrap">
      <Head n="02" kicker="A sewing date that fits your life" id="hpc-ways-title" className="hp-head-center">Come for the project. <span className="hp-yellow">Stay for the company.</span></Head>
      <div className="hpc-ways-grid">
        {WAYS.map((w, i) => <motion.article key={w.key} className="hpc-way" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * 0.12, duration: 0.6, ease: EASE }}>
          <span className={`hp-arch hp-arch-${w.arch} hpc-way-arch`}><span><img src={w.img} alt="" style={{ objectPosition: w.pos }} /></span>{w.badge}</span>
          <div className="hpc-way-body">
            <span className="hp-tag">{w.tag}</span>
            <h3>{w.title}</h3>
            <p>{w.text}</p>
            <ol>{w.list.map((t, k) => <li key={t}><span>{"ABC"[k]}</span>{t}</li>)}</ol>
            <p className="hpc-way-foot">{w.foot}</p>
          </div>
        </motion.article>)}
      </div>
    </div>
  </Band>;
}

/* ─── 03 Company ─── */

const LABELS = [
  { tone: "white", t: "Share your makes", d: "Post the finished thing. Post the wonky first try too." },
  { tone: "yellow", t: "Join a challenge", d: "A little push to finally cut into the good fabric." },
  { tone: "blue", t: "Celebrate the little wins", d: "First zipper? First quilt? We’re throwing confetti." },
] as const;

function Company() {
  return <Band color="#f7a1c4" seed={89} className="hp-words hpc-company" label="hpc-company-title" top={false}>
    <div className="hp-wrap">
      <Head n="03" kicker="People who get it" id="hpc-company-title" className="hp-head-center">Your project pile is <span className="hp-blue">in good company.</span></Head>
      <p className="hpc-company-lede">I built this club for people who love making things. Share your makes, join a challenge, and celebrate the little wins with me and other sewists who get why you needed just one more piece of fabric.</p>
      <div className="hp-words-grid hpc-labels">
        {LABELS.map((l, i) => <motion.div key={l.t} className={`hp-label hp-label-${l.tone}`} initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: [-3, 2, -1.5][i] }} viewport={VIEW} transition={{ delay: i * 0.12, duration: 0.6, ease: EASE }}>
          <b className="hpc-label-t">{l.t}</b>
          <p className="hp-pen">{l.d}</p>
        </motion.div>)}
      </div>
      <motion.blockquote className="hpc-quote" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: 0.3, duration: 0.6, ease: EASE }}>
        <p>“It’ll be fun, slightly unhinged, and very thread-covered.”</p>
        <footer className="hp-sig-name">Chelsea</footer>
      </motion.blockquote>
    </div>
  </Band>;
}

/* ─── 04 Membership ─── */

const YARDAGE = [["Live classes", "2 a month"], ["Replays", "Every one"], ["Billing", "Monthly"], ["Cancel", "Anytime"]];

function Membership() {
  return <section id="join" className="hp-sec hpc-join" aria-labelledby="hpc-join-title">
    <div className="hp-wrap hpc-join-grid">
      <div>
        <Head n="04" kicker="I’ve saved you a chair" id="hpc-join-title">A little time to make. <span className="hp-blue">A lot to look forward to.</span></Head>
        <p className="hpc-month-lede">I’ll bring the project and a little help along the way. You bring your fabric, your questions, and a little time for yourself. Let’s make something we’re excited about.</p>
        <p className="hp-pen hpc-chaos">Come as you are. Bring your creative chaos. <InkHeart delay={0.8} /></p>
        <div className="hpc-friend">
          <p className="hp-tag">Just want to cheer me on?</p>
          <p className="hpc-friend-t">Thread Friend · $3/month</p>
          <p>Help me keep creating. You’ll get merch discounts and occasional codes. This tier doesn’t include my classes or replays.</p>
          <a href={THREAD_FRIEND_URL} className="hp-textlink">Become a Thread Friend <ArrowRight size={15} aria-hidden="true" /></a>
        </div>
      </div>

      <div className="hp-env-wrap hpc-env-wrap">
        <motion.div className="hp-tissue" aria-hidden="true" initial={{ opacity: 0, rotate: 0 }} whileInView={{ opacity: 1, rotate: 6 }} viewport={VIEW} transition={{ delay: 0.3, duration: 0.8, ease: EASE }}>
          <svg viewBox="0 0 300 400" preserveAspectRatio="none"><path d="M30 30 L250 18 L274 200 L240 380 L40 372 L20 200 Z" /><path className="hp-grain" d="M150 90 V310 M136 108 L150 90 L164 108 M136 292 L150 310 L164 292" /></svg>
          <span>Piece B · Cut 1 · On fold</span>
        </motion.div>
        <motion.article className="hp-env hpc-env" aria-label="Loose Threads Club membership, $25 a month" initial={{ opacity: 0, y: 40, rotate: 2 }} whileInView={{ opacity: 1, y: 0, rotate: -2 }} viewport={VIEW} transition={{ type: "spring", stiffness: 110, damping: 16 }}>
          <header className="hp-env-top">
            <b>LTC</b>
            <span><small>Pattern no. 025 · Membership</small>Loose Threads Club</span>
            <em>One size<br />fits all</em>
          </header>
          <div className="hpc-env-body">
            <p className="hp-env-price"><b>$25</b>/month</p>
            <p className="hpc-env-intro">Sew with me live or catch up on your own time.</p>
            <p className="hp-env-label">Pattern includes</p>
            <ol>{CLUB_INCLUDES.map((t, i) => <li key={t}><span>{"ABCDEF"[i]}</span>{t}</li>)}</ol>
            <a href={JOIN_URL} className="hp-btn hp-btn-blue hpc-env-join">Join the club on Patreon <ArrowRight size={18} aria-hidden="true" /></a>
          </div>
          <dl className="hp-env-yard">{YARDAGE.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </motion.article>
        <p className="hpc-fine hpc-env-fine">Billed monthly. Cancel anytime through Patreon. Patterns and supplies are separate unless stated.</p>
      </div>
    </div>
  </section>;
}

/* ─── 05 FAQ ─── */

function Faq() {
  return <Band color="#f6d64a" seed={103} className="hp-where hpc-faq" label="hpc-faq-title">
    <div className="hp-wrap hpc-faq-grid">
      <div>
        <Head n="05" kicker="Before you pull up a chair" id="hpc-faq-title">A few things <span className="hp-blue">you might wonder.</span></Head>
        <p className="hp-pen hpc-faq-pen">Still getting to know me?</p>
        <a href={SOCIALS.youtube.url} target="_blank" rel="noreferrer" className="hp-textlink hpc-faq-link">Watch my YouTube videos <ArrowUpRight size={15} aria-hidden="true" /></a>
      </div>
      <div className="hpc-faq-list">
        {CLUB_FAQS.map((f, i) => <details key={f.question}>
          <summary><span className="hpc-faq-no">Q{i + 1}</span><b>{f.question}</b><span className="hpc-faq-plus" aria-hidden="true" /></summary>
          <p>{f.answer}</p>
        </details>)}
      </div>
    </div>
  </Band>;
}

/* ─── 06 Chair ─── */

function Chair() {
  const [next] = nextClasses(1);
  return <section className="hp-sec hp-loop hpc-chair" aria-labelledby="hpc-chair-title">
    <svg className="hp-loop-paper" viewBox="0 0 1600 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <Torn seed={107} fill="#1d3fc8" edge={[[0, 430], [120, 470], [230, 560], [290, 700]]} close={[[0, 700]]} />
      <Torn seed={119} fill="#f6d64a" edge={[[1600, 120], [1500, 110], [1400, 60], [1350, 0]]} close={[[1600, 0]]} />
    </svg>
    <svg className="hp-loop-dash" viewBox="0 0 1600 700" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <path d="M40 300 C 120 250, 220 300, 260 380 S 300 520, 420 560" />
      <path d="M1580 300 C 1500 340, 1440 300, 1400 240 S 1300 150, 1200 170" />
    </svg>
    <motion.div className="hp-loop-arch" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.7, ease: EASE }}>
      <p className="hp-kicker"><span>06</span>Pull up a chair</p>
      <h2 id="hpc-chair-title">Your next project <span className="hp-blue">starts {datePart(next, { weekday: "long" })}.</span></h2>
      <p className="hp-loop-lede">Next live class: <b>{datePart(next, { weekday: "short", month: "short", day: "numeric" })} · 7 PM MT</b>. Can’t make it? The replay will be waiting.</p>
      <a href={JOIN_URL} className="hp-btn hp-btn-blue hpc-chair-join">Come sew with me <ArrowRight size={18} aria-hidden="true" /></a>
      <p className="hpc-chair-fine">$25/month · cancel anytime</p>
      <p className="hp-sig-name hp-loop-sig">Chelsea</p>
    </motion.div>
  </section>;
}

export function ClubPattern() {
  useFonts();
  return <div className="h5 h5-pal-pattern hp hpc">
    <Hero />
    <ThisMonth />
    <TwoWays />
    <Company />
    <Membership />
    <Faq />
    <Chair />
    <Footer />
  </div>;
}
